import GlueFrame from 'glueframe';
import BaseBehavior from '../../../framework/base/base';
import {
	initBehaviors,
	destroyBehaviors,
	resetBehaviorNames
} from '../../../shared/utilities/behaviors';
import { getVideoUrl, getQrCode } from '../../../shared/utilities/urlHelpers';
import {
	isDevice,
	deviceDetection,
	addDeviceDocumentClass,
	removeDeviceDocumentClass
} from '../../../shared/utilities/deviceDetection';
import { debounce } from '../../../shared/utilities/debounce';
import { onEffectEndEvent } from '../../../shared/utilities/effectsEndEventHandler';
import createGlueframeTracking from '../../../shared/utilities/video-glueframe-tracking';
import { screenSmallerThan } from '../../../shared/utilities/screenSize';
import {
	addTrapFocusListener,
	removeTrapFocusListener
} from '../../../shared/utilities/trap-focus';

const classes = {
	open: 'v2-lightbox--open',
	video: 'v2-lightbox--video',
	image: 'v2-lightbox--image'
};

export default class Lightbox extends BaseBehavior {
	constructor(element) {
		super(element);

		this.initDomCache();
		this.initData();
		this.addEventListeners();
		this.addSubscriptions();
	}

	initData() {
		this.title = '';
		this.cssClass = '';
		this.videoObject = null;
	}

	addEventListeners() {
		this.addEventListener(this.dom.close, 'click.close', e => {
			e.preventDefault();
			this.close();
		});

		this.addEventListener(this.dom.overlay, 'click.overlay', e => {
			e.preventDefault();
			this.close();
		});

		this.addEventListener(
			document.body,
			'load.img',
			event => {
				if (event.target.tagName === 'IMG' && this.dom.content.contains(event.target)) {
					const elem = this.dom.content.querySelector('.v2-lightbox__loading-indicator');

					if (elem) {
						elem.classList.add('v2-lightbox__loading-indicator--hidden');
					}
				}
			},
			true // <-- useCapture
		);
	}

	removeEventListeners() {
		this.removeEventListener(this.dom.close, 'click.close');
		this.removeEventListener(this.dom.overlay, 'click.overlay');
		this.removeEventListener(document.body, 'load.img');
	}

	addSubscriptions() {
		this.subscribe('/lightbox/open', ({ cssClass, target, title = '', type, rte, option } = {}) => {
			if (cssClass) {
				this.cssClass = cssClass;
				this.element.classList.add(this.cssClass);
			}

			switch (type) {
				case 'html':
					this.removeVideoClass();
					this.removeImageClass();
					this.updateContentFromHtml(target);
					break;
				case 'video':
					this.removeImageClass();
					this.updateContentWithVideo(getVideoUrl(target));
					break;
				case 'videoUrl':
					this.removeImageClass();
					this.updateContentWithVideo(target);
					break;
				case 'qrcode':
					this.removeImageClass();
					this.updateContentWithQrCode(target, rte);
					break;
				case 'image':
					this.removeVideoClass();
					this.updateContentWithImage(option);
					break;
				default:
				case 'id':
					this.removeVideoClass();
					this.removeImageClass();
					this.updateContentFromId(target);
					break;
			}
			this.open();
			this.updateTitle(title);
		});
		this.subscribe('/lightbox/close', this.close.bind(this));
	}

	removeSubscriptions() {
		this.unsubscribe('/lightbox/close');
		this.unsubscribe('/lightbox/open');
	}

	addKeyListeners() {
		this.addEventListener(document, 'keyup', e => {
			if (e.keyCode === 27) {
				this.close();
			}
		});
	}

	removeKeyListeners() {
		this.removeEventListener(document, 'keyup');
		removeTrapFocusListener(this);
	}

	updateContentFromId(target) {
		const element = document.getElementById(target);

		if (element) {
			this.dom.content.innerHTML =
				element[element.nodeName === 'SCRIPT' ? 'innerHTML' : 'outerHTML'];
		}
	}

	updateContentFromHtml(target) {
		this.dom.content.innerHTML = '';
		this.dom.content.appendChild(target);
	}

	updateContentWithQrCode(target, rte) {
		const url = target || window.location.href;
		const rteOutput = rte ? `<div class="rte--output">${unescape(rte)}</div>` : '';
		const qrCode = getQrCode(encodeURIComponent(url));

		this.element.classList.add(classes.image);
		this.dom.content.innerHTML = rteOutput + `<img src="${qrCode}" alt="" />`;
	}

	updateContentWithVideo(target) {
		this.element.classList.add(classes.video);

		this.dom.content.innerHTML = `<div style="margin:0 auto"><div class="v2-lightbox-video">
				<iframe src="${target}"
					border="0"
					allowfullscreen="1"
					mozallowfullscreen="1"
					webkitallowfullscreen="1"
					allow="autoplay"
					title="Video"></iframe>
			</div></div>`;

		const iframe = this.dom.content.querySelector('iframe');
		if (screenSmallerThan('sm') && iframe) {
			this.addEventListener(iframe, 'click', e => {
				e.preventDefault();
				this.enlargeIframe(iframe);
			});
		}
		iframe.onload = () => {
			this.videoObject = new GlueFrame(this.dom.content.querySelector('iframe'), 'Player');
			const listenToPlayer = createGlueframeTracking();
			listenToPlayer(this.videoObject);
			// eslint-disable-next-line no-underscore-dangle
			window._gluePlayers = window._gluePlayers || [];
			// eslint-disable-next-line no-underscore-dangle
			window._gluePlayers.push(this.videoObject);
			this.videoObject.set('playing', true);
			iframe.setAttribute('data-twentythree-adobe', 'yes');
		};

		this.adjustVideoSize();
		this.addEventListener(
			window,
			'resize',
			debounce(() => this.adjustVideoSize(), 250)
		);
		/* manually clicking on iframe to open iframe in fullscreen, otherwise it won't
		 open due to error(API can only be initiaited by user gesture) */
		iframe && iframe.click();
	}

	isFullScreen() {
		return Boolean(
			document.fullscreenElement ||
				document.webkitFullscreenElement ||
				document.mozFullScreenElement
		);
	}

	enlargeIframe(iframe) {
		if (this.isFullScreen()) return;

		if (iframe.requestFullscreen) {
			return iframe.requestFullscreen().catch(err => {       // eslint-disable-line
				alert(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
			});
		}
		if (iframe.mozRequestFullScreen) {
			return iframe.mozRequestFullScreen().catch(err => {    // eslint-disable-line
				alert(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
			});
		}
		if (iframe.webkitRequestFullscreen) {
			return iframe.webkitRequestFullscreen().catch(err => {    // eslint-disable-line
				alert(`Error attempting to enable fullscreen mode: ${err.message} (${err.name})`);
			});
		}
	}

	/**
	 * Update content with Image
	 * @param {*} target
	 */
	updateContentWithImage(option) {
		this.element.classList.add(classes.image);
		let html = `<div class="popup__content v2-maa0">
				<div class="v2-lightbox-image">`;
		if (option.image) {
			html += `<img class="v2-picture v2-picture--background v2-picture--fit-cover v2-picture--pos-cc medium medium--col2 small small-desktop--col4" 
					src="${option.image}" alt='${option.title}' />`;
		}
		html += `</div>
				<div class="v2-flexbox v2-align--start collection-popup-desc">
					<h4 class="v2-maa0">${option.title} &nbsp;&nbsp;${option.label} </h4>
				</div>
			</div>`;

		this.dom.content.innerHTML = html;
	}

	adjustVideoSize() {
		const { content } = this.dom;
		const wrapper = content.firstElementChild;
		const iframe = wrapper.firstElementChild.firstElementChild;
		wrapper.style.width = 'auto';
		if (iframe.offsetHeight > content.offsetHeight) {
			wrapper.style.width =
				(content.offsetHeight * iframe.offsetWidth) / iframe.offsetHeight + 'px';
		}
	}

	removeVideoClass() {
		this.element.classList.remove(classes.video);
	}

	removeImageClass() {
		this.element.classList.remove(classes.image);
	}

	updateTitle(title) {
		if (typeof title === 'object') {
			this.dom.title.appendChild(title);
		} else {
			this.dom.title.innerText = title;
		}
	}

	open() {
		document.body.classList.add(classes.open);

		if (isDevice(deviceDetection.mobile)) {
			addDeviceDocumentClass(deviceDetection.mobile);
		}

		this.addKeyListeners();
		resetBehaviorNames({ context: this.dom.content });
		initBehaviors({ context: this.dom.content });
		if (window.picturefill) {
			window.picturefill({ elements: this.dom.content.querySelectorAll('picture img') });
		}

		addTrapFocusListener(this, { selector: '.v2-lightbox__content' });
	}

	close() {
		this.publish('/lighbox/after-close');
		document.body.classList.remove(classes.open);
		removeDeviceDocumentClass(deviceDetection.mobile);
		this.removeKeyListeners();
		this.removeEventListener(window, 'resize');
		destroyBehaviors({ context: this.dom.content });
		onEffectEndEvent(this.element, () => {
			this.dom.content.innerHTML = '';
			this.dom.title.innerHTML = '';
			if (this.cssClass) {
				this.element.classList.remove(this.cssClass);
			}
		});
		removeTrapFocusListener(this);
	}

	cleanup() {
		this.removeEventListeners();
		this.removeSubscriptions();
		this.removeKeyListeners();
	}
}
